<template>
  <!-- 宣传卡片：悬浮在右上角，可拖动、可折叠（» 收起为医生形象，点医生再展开） -->
  <div
      class="ai-agent-entry-wrap"
      :class="{ 'is-dragging': dragging }"
      :style="dragStyle"
      @mousedown="onDragStart"
      @dragstart.prevent
      @click.capture="onClickCapture">
    <transition name="ai-agent-switch" mode="out-in">
      <!-- 展开：完整卡片；只有中间文案区点击才打开选项目/分析弹窗 -->
      <div v-if="!collapsed" key="full" class="ai-agent-entry">
        <div class="ai-agent-entry__side">
          <button type="button" class="ai-agent-entry__chevron" title="收起"
                  @mousedown.stop="noop"
                  @click.stop="collapsed = true">»
          </button>
          <span class="ai-agent-entry__vertical">凌博士</span>
        </div>

        <div class="ai-agent-entry__main" @click="$emit('open')">
          <div class="ai-agent-entry__title">凌博士组学智能体 <b>Pro</b></div>
          <div class="ai-agent-entry__hint">
            选择项目与分析，<span style="color: #5daf34">立即体验<i class="el-icon-right" style="color: #5daf34"></i></span>
          </div>
        </div>

        <div class="ai-agent-entry__right">
          <span class="ai-agent-entry__glow"></span>
          <img :src="doctor" class="ai-agent-entry__img" alt="凌博士组学智能体" draggable="false"/>
          <span class="ai-agent-entry__speed"><em>0</em>秒抓取</span>
        </div>
      </div>

      <!-- 收起：只留医生形象，点击重新展开 -->
      <div v-else key="mini" class="ai-agent-entry-mini" title="展开凌博士组学智能体"
           @click="collapsed = false">
        <img :src="doctor" class="ai-agent-entry-mini__img" alt="凌博士组学智能体" draggable="false"/>
        <span class="ai-agent-entry-mini__badge">Pro</span>
      </div>
    </transition>
  </div>
</template>

<script>
import doctor from '@/assets/aiAgentDoctor.png'

const POSITION_KEY = 'aiAgentEntryPosition'

export default {
  name: 'AiAgentEntry',
  data() {
    return {
      doctor,
      // 是否收起为只留医生形象
      collapsed: true,
      // 拖动后的位置（{ left, top }），为空时用 CSS 默认的右上角定位
      dragStyle: {},
      dragging: false
    }
  },
  watch: {
    // 展开/收起后尺寸变了，按当前所处位置自动选择锚点：
    // 靠近左半边 → 保持左边缘向右展开；否则 → 保持右边缘向左展开
    collapsed() {
      if (!this.dragStyle || !this.dragStyle.top) return
      // 此时 DOM 还是变化前的尺寸，先取旧位置
      const prev = this.$el.getBoundingClientRect()
      const anchorLeft = (prev.left + prev.width / 2) < (window.innerWidth / 2)
      this.$nextTick(() => {
        const el = this.$el
        if (!el) return
        const width = el.offsetWidth
        const height = el.offsetHeight
        const vw = window.innerWidth
        const vh = window.innerHeight
        let left = anchorLeft
            ? prev.left                                   // 左锚点：向左不动，向右生长
            : vw - (vw - prev.right) - width              // 右锚点：右边缘不动，向左生长
        left = Math.min(Math.max(0, left), Math.max(0, vw - width))
        const top = Math.min(Math.max(0, prev.top), Math.max(0, vh - height))
        this.dragStyle = {left: left + 'px', top: top + 'px', right: 'auto'}
      })
    }
  },
  created() {
    this._dragState = null
    this._didDrag = false
    this._onMove = this.onDragMove.bind(this)
    this._onUp = this.onDragEnd.bind(this)
  },
  mounted() {
    this.restorePosition()
  },
  beforeDestroy() {
    document.removeEventListener('mousemove', this._onMove)
    document.removeEventListener('mouseup', this._onUp)
    document.body.style.userSelect = ''
  },
  methods: {
    // 阻止 mousedown 冒泡到拖动逻辑（» 按钮用）
    noop() {
    },
    onDragStart(event) {
      if (event.button !== 0) return
      // 阻止浏览器原生拖拽（图片幽灵）与文本选择
      event.preventDefault()
      const rect = this.$el.getBoundingClientRect()
      this._dragState = {
        startX: event.clientX,
        startY: event.clientY,
        left: rect.left,
        top: rect.top,
        moved: false
      }
      this._didDrag = false
      document.addEventListener('mousemove', this._onMove)
      document.addEventListener('mouseup', this._onUp)
    },
    onDragMove(event) {
      const state = this._dragState
      if (!state) return
      const dx = event.clientX - state.startX
      const dy = event.clientY - state.startY
      // 小于阈值视为点击，不触发拖动
      if (!state.moved && Math.abs(dx) + Math.abs(dy) < 4) return
      state.moved = true
      this._didDrag = true
      this.dragging = true
      document.body.style.userSelect = 'none'
      const width = this.$el.offsetWidth
      const height = this.$el.offsetHeight
      const maxLeft = Math.max(0, window.innerWidth - width)
      const left = Math.min(Math.max(0, state.left + dx), maxLeft)
      const top = Math.min(Math.max(0, state.top + dy), Math.max(0, window.innerHeight - height))
      // 以“距右边距离”为准：收起/展开时右边缘不动，卡片向左生长，避免展开后超出屏幕
      const right = Math.max(0, window.innerWidth - left - width)
      this.dragStyle = {right: right + 'px', top: top + 'px', left: 'auto'}
    },
    onDragEnd() {
      const state = this._dragState
      this._dragState = null
      this.dragging = false
      document.removeEventListener('mousemove', this._onMove)
      document.removeEventListener('mouseup', this._onUp)
      document.body.style.userSelect = ''
      if (state && state.moved) {
        this.savePosition()
        // 兜底：如果在组件外松开，浏览器不会补发 click，这里把标记复位，
        // 避免下一次正常点击被误拦
        setTimeout(() => {
          this._didDrag = false
        }, 100)
      }
    },
    // 拖动结束时浏览器还会补发一次 click，这里拦掉，避免误开弹窗
    onClickCapture(event) {
      if (this._didDrag) {
        this._didDrag = false
        event.stopPropagation()
        event.preventDefault()
      }
    },
    clampPosition() {
      const el = this.$el
      if (!el) return
      const rect = el.getBoundingClientRect()
      const width = rect.width
      const height = rect.height
      const maxRight = Math.max(0, window.innerWidth - width)
      // 距右边距离限制在 [0, 视口宽 - 卡片宽]，保证左右都可见
      const right = Math.min(Math.max(0, window.innerWidth - rect.right), maxRight)
      const top = Math.min(Math.max(0, rect.top), Math.max(0, window.innerHeight - height))
      this.dragStyle = {right: right + 'px', top: top + 'px', left: 'auto'}
    },
    savePosition() {
      try {
        const rect = this.$el.getBoundingClientRect()
        // 记录“距右边距离”和 top：展开/收起时右边缘保持一致
        window.localStorage.setItem(POSITION_KEY, JSON.stringify({
          right: window.innerWidth - rect.right,
          top: rect.top
        }))
      } catch (e) {
        // 忽略存储失败
      }
    },
    restorePosition() {
      try {
        const saved = JSON.parse(window.localStorage.getItem(POSITION_KEY) || 'null')
        if (!saved) return
        let right = null
        if (typeof saved.right === 'number') {
          right = saved.right
        } else if (typeof saved.left === 'number') {
          // 兼容旧版本保存的 left 坐标
          right = Math.max(0, window.innerWidth - saved.left - (this.$el.offsetWidth || 0))
        }
        if (right !== null && typeof saved.top === 'number') {
          this.dragStyle = {right: right + 'px', top: saved.top + 'px', left: 'auto'}
          this.$nextTick(this.clampPosition)
        }
      } catch (e) {
        // 忽略读取失败
      }
    }
  }
}
</script>

<style scoped>
/* 悬浮在页面右上角：fixed 定位，可拖动，不占文档流 */
.ai-agent-entry-wrap {
  position: fixed;
  top: 150px;
  right: 22px;
  z-index: 1500;
  cursor: grab;
  touch-action: none;
}

.ai-agent-entry-wrap.is-dragging {
  cursor: grabbing;
}

/* 禁止图片被浏览器原生拖动 */
.ai-agent-entry img {
  -webkit-user-drag: none;
  user-select: none;
}

/* ---------- 展开：完整卡片 ---------- */
.ai-agent-entry {
  display: flex;
  align-items: stretch;
  width: 310px;
  height: 96px;
  border-radius: 14px;
  overflow: hidden;
  user-select: none;
  background: linear-gradient(135deg, #ffffff 0%, #f2f8ff 100%);
  border: 1px solid rgba(63, 157, 254, .25);
  box-shadow: 0 8px 20px rgba(20, 60, 120, .14);
  transition: transform .25s ease, box-shadow .25s ease;
}

.ai-agent-entry:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 28px rgba(20, 60, 120, .22);
}

/* 左侧蓝色竖条：折叠按钮 + 竖排“凌博士” */
.ai-agent-entry__side {
  flex: none;
  width: 35px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  padding: 8px 0;
  color: #fff;
  background: #2f7fd1 ;
}

.ai-agent-entry__chevron {
  padding: 0;
  border: 0;
  background: transparent;
  color: #fff;
  font-size: 17px;
  font-weight: 700;
  line-height: 1;
  opacity: .85;
  cursor: pointer;
  transition: opacity .2s, transform .2s;
}

.ai-agent-entry__chevron:hover {
  opacity: 1;
  transform: translateX(2px);
}

.ai-agent-entry__vertical {
  writing-mode: vertical-rl;
  letter-spacing: 3px;
  font-size: 13px;
  font-weight: 600;
  opacity: .95;
}

/* 中间文案：点击这里才打开选项目/分析弹窗 */
.ai-agent-entry__main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 7px;
  padding: 0 0px 0 14px;
  cursor: pointer;
}

.ai-agent-entry__title {
  font-size: 15px;
  font-weight: 700;
  color: #14325a;
  letter-spacing: .2px;
  white-space: nowrap;
}

.ai-agent-entry__title b {
  color: #0f5fbf;
  font-weight: 800;
}

.ai-agent-entry__hint {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #4d6b8f;
  white-space: nowrap;
}

.ai-agent-entry__hint i {
  font-size: 12px;
  color: #2f86e8;
  transition: transform .2s;
}

.ai-agent-entry:hover .ai-agent-entry__hint i {
  transform: translateX(3px);
}

/* 右侧：医生 + 0秒抓取 */
.ai-agent-entry__right {
  position: relative;
  flex: none;
  width: 100px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  padding: 2px 6px 5px 0;
}

.ai-agent-entry__glow {
  position: absolute;
  right: 8px;
  bottom: 12px;
  width: 74px;
  height: 74px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(90, 170, 255, .35) 0%, rgba(90, 170, 255, 0) 70%);
}

.ai-agent-entry__img {
  position: relative;
  width: 76px;
  height: 76px;
  object-fit: contain;
  filter: drop-shadow(0 4px 8px rgba(20, 60, 120, .18));
  transition: transform .25s;
}

.ai-agent-entry:hover .ai-agent-entry__img {
  transform: translateY(-2px) scale(1.03);
}

.ai-agent-entry__speed {
  position: relative;
  margin-top: -4px;
  padding: 0 6px;
  border-radius: 8px;
  background: rgba(240, 138, 29, .12);
  font-size: 10px;
  font-weight: 600;
  color: #1f5fa8;
  white-space: nowrap;
}

.ai-agent-entry__speed em {
  margin-right: 1px;
  color: #f08a1d;
  font-size: 12px;
  font-style: normal;
  font-weight: 700;
}

/* ---------- 收起：只留医生形象 ---------- */
.ai-agent-entry-mini {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  cursor: pointer;
  user-select: none;
  background: linear-gradient(135deg, #ffffff 0%, #eef6ff 100%);
  border: 1px solid rgba(63, 157, 254, .3);
  box-shadow: 0 8px 20px rgba(20, 60, 120, .18);
  transition: transform .2s, box-shadow .2s;
}

.ai-agent-entry-mini:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 26px rgba(20, 60, 120, .26);
}

.ai-agent-entry-mini__img {
  width: 52px;
  height: 52px;
  object-fit: contain;
}

.ai-agent-entry-mini__badge {
  position: absolute;
  top: -2px;
  right: -6px;
  padding: 1px 5px;
  border-radius: 8px;
  background: #2f86e8;
  color: #fff;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: .3px;
}

/* 展开/收起切换动画 */
.ai-agent-switch-enter-active,
.ai-agent-switch-leave-active {
  transition: opacity .15s ease, transform .15s ease;
}

.ai-agent-switch-enter,
.ai-agent-switch-leave-to {
  opacity: 0;
  transform: scale(.95);
}
</style>
